// plan-modusign-style-acceptance §Phase1 — 새 양식 생성 모달.
// 이름 + 문서 유형 입력 → 생성 → 디자이너로 이동.
// PDF 업로드는 디자이너 화면에서 처리.

import { useState, type CSSProperties } from 'react';
import { useMutation } from '@tanstack/react-query';
import { SignatureDocumentType } from '@bhops/shared';
import type { SignatureDocumentType as SigDocType } from '@bhops/shared';
import { signatureTemplatesApi } from '@/api/signatureTemplates';
import { extractApiError } from '@/api/client';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Form';
import { useToast } from '@/components/Toast';

const DOC_TYPE_OPTIONS: { value: SigDocType; label: string }[] = [
  { value: SignatureDocumentType.DELIVERY_RECEIPT, label: '인수증' },
  { value: SignatureDocumentType.INSTALLATION_TRAINING, label: '설치·교육 인수증' },
  { value: SignatureDocumentType.REMOTE_SERVICE_CONSENT, label: '데이터 수집·이용 동의서' },
  { value: SignatureDocumentType.CUSTOM, label: '사용자 정의' },
];

const field: CSSProperties = { marginBottom: 16 };
const label: CSSProperties = { display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 };
const input: CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 6,
  border: '1px solid var(--border-default)',
  fontSize: 13,
  boxSizing: 'border-box',
};

interface Props {
  onClose: () => void;
  onCreated: (id: string) => void;
}

export function SignTemplateNewModal({ onClose, onCreated }: Props) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [docType, setDocType] = useState<SigDocType>(SignatureDocumentType.DELIVERY_RECEIPT);

  const mutation = useMutation({
    mutationFn: () => signatureTemplatesApi.create({ name: name.trim(), documentType: docType }),
    onSuccess: ({ id }) => {
      toast.success('양식을 만들었습니다. PDF를 올려 주세요.');
      onCreated(id);
    },
    onError: (err) => toast.error(extractApiError(err)),
  });

  return (
    <Modal open title="새 서명 양식" onClose={onClose} width={520}>
      {/* FB §7 — 모달 폭(520)에 맞춰 본문 가변(100%). 기존 minWidth:360 완화. */}
      <div style={{ width: '100%', padding: '4px 0' }}>
        <div style={field}>
          <label style={label}>양식 이름 *</label>
          <input
            style={input}
            placeholder="예: 인수증 v260205"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
            autoFocus
          />
        </div>

        <div style={field}>
          <label style={label}>문서 유형 *</label>
          <select
            style={input}
            value={docType}
            onChange={(e) => setDocType(e.target.value as SigDocType)}
          >
            {DOC_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 20px' }}>
          만든 뒤 디자이너에서 베이스 PDF를 올리고 서명 칸과 글자 칸을 놓을 수 있습니다.
        </p>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            취소
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={!name.trim() || mutation.isPending}>
            양식 생성
          </Button>
        </div>
      </div>
    </Modal>
  );
}
